import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {User} from '../../../_data/admin/user';
import {UsersService} from '../../../_services/data/users.service';
import {I18nService} from '../../../_services/gui/i18n.service';
import {faEdit, faKey, faTrash, faUserAltSlash, faUserCheck} from '@fortawesome/free-solid-svg-icons';
import {HttpResponse} from '@angular/common/http';
import {filter, map, takeUntil, tap} from 'rxjs/operators';
import {reformatDate} from '../../../_utils/lang';
import {exists} from '../../../_utils/operators';
import {Observable, of, Subject} from 'rxjs';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {shortDaysOfWeek} from 'src/app/_constants/constants';
import {AuthenticationService} from '../../../_services/authentication.service';
import {GuiPermissionsService} from '../../../_services/gui/gui-permissions.service';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {IconButtonComponent} from '../../../common/icon-button/icon-button.component';
import {UserDeviceAdminComponent} from '../../../mfa/user-device-admin/user-device-admin.component';
import {AsyncPipe} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'app-user-details',
    templateUrl: './user-details.component.html',
    styleUrls: ['./user-details.component.scss'],
    imports: [RouterLink, FaIconComponent, IconButtonComponent, UserDeviceAdminComponent, AsyncPipe, TranslateModule]
})
export class UserDetailsComponent implements OnInit, OnDestroy {

  faEdit = faEdit as IconProp;
  faUserAltSlash = faUserAltSlash as IconProp;
  faUserCheck = faUserCheck as IconProp;
  faTrash = faTrash as IconProp;
  faKey = faKey as IconProp;

  reformatDate = reformatDate;

  shortDaysOfWeek = shortDaysOfWeek;

  user: User;
  userFailedToLoad = false;

  userParam: string;
  timeZones: Map<string, string>;

  canEditUser: boolean = false;
  canDisableUser: boolean = false;
  canEnableUser: boolean = false;
  canTerminateUser: boolean = false;
  canResetPassword: boolean = false;
  canChangePrimaryMfa: boolean = false;
  canRemoveMfa: boolean = false;

  readonly guiPermissionsService = inject(GuiPermissionsService);
  readonly usersService = inject(UsersService);
  readonly authService = inject(AuthenticationService);

  private destroy$: Subject<void> = new Subject<void>();

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly i18nService = inject(I18nService);

  constructor() {
    this.i18nService.timeZones.pipe(takeUntil(this.destroy$)).subscribe(value => this.timeZones = value);
    this.userParam = this.route.snapshot.params.userId;
    this.usersService.getUserDetails(this.userParam).pipe(
      map(response => response.body),
      tap(userDetails => console.debug('User Details', userDetails))
    ).subscribe(u => {
      this.user = u;

      if(!!this.user) {
        this.guiPermissionsService.canEditUser(u).subscribe(v => this.canEditUser = v);
        this.guiPermissionsService.canDisableUser(u).subscribe(v => this.canDisableUser = v);
        this.guiPermissionsService.canEnableUser(u).subscribe(v => this.canEnableUser = v);
        this.guiPermissionsService.canTerminateUser(u).subscribe(v => this.canTerminateUser = v);
        this.guiPermissionsService.canResetPassword(u).subscribe(v => this.canResetPassword = v);

        this.guiPermissionsService.canChangePrimaryMfa(u).subscribe(v => this.canChangePrimaryMfa = v);
        this.guiPermissionsService.canRemoveMfa(u).subscribe(v => this.canRemoveMfa = v);
      } else {
        this.userFailedToLoad = true;
      }
    });
  }

  private onUserUpdated(): void {
    this.usersService.getUserDetails(this.user.userName).subscribe((response: HttpResponse<User>) => this.user = response.body);
  }

  ngOnInit() {
    this.i18nService.setTitle('title.admin.users.detail');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  disable(ariaLabelledBy: string, container: HTMLElement) {
    this.usersService.confirmDisableUser(this.user, ariaLabelledBy, container).pipe(exists()).subscribe(() => this.onUserUpdated());
  }

  enable(ariaLabelledBy: string, container: HTMLElement) {
    this.usersService.confirmEnableUser(this.user, ariaLabelledBy, container).pipe(exists()).subscribe(() => this.onUserUpdated());
  }

  reset(ariaLabelledBy: string, container: HTMLElement) {
    this.usersService.confirmResetUserPassword(this.user, ariaLabelledBy, container).pipe(exists()).subscribe(() => this.onUserUpdated());
  }

  terminate(ariaLabelledBy: string, container: HTMLElement) {
    this.usersService.confirmTerminateUser(this.user, ariaLabelledBy, container).pipe(filter(success => success)).subscribe(() => this.router.navigateByUrl('/admin/users'));
  }

  branchDetails(): string[] {
    return this.user.branchDetails && this.user.branchDetails.map(b => b.branchName) || [];
  }

  accessTime(dayNum: number): Observable<string> {
    const tr = this.user.timeRestrictions.find(t => Number(t.dayOfWeek) === dayNum);
    if(!tr) {
      return this.i18nService.getTranslation('content.not-available-short');
    } else if(tr.allDay === 1) {
      return this.i18nService.getTranslation('content.24-hours');
    } else if(tr.allDay === -1) {
      return this.i18nService.getTranslation('content.no-access');
    } else {
      return of(`${tr.timeFrom} - ${tr.timeTo}`);
    }
  }

}
